import React, { useEffect, useState } from "react";
import "./App.css";
import { useDispatch, useSelector } from "react-redux";
import Login from "./modules/auth/Login";
import {
  // BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  unstable_HistoryRouter as Router,
  useLocation,
} from "react-router-dom";
import { Button, Form, Input, Layout, Switch } from "antd";
import { Content, Footer, Header } from "antd/lib/layout/layout";
import Dashboard from "./modules/sections/dashboard/Dashboard";
import SidebarLayout from "./modules/layout/sidebar/SidebarLayout";
import { Head } from "./modules/layout/Head";
import Foot from "./modules/layout/Foot";
import ProjectSetup from "./modules/sections/configuration/project-setup/ProjectSetup";
import TableSetup from "./modules/sections/configuration/table-setup/TableSetup";
import Extraction from "./modules/sections/operation/extraction/Extraction";
import LegacyMapping from "./modules/sections/operation/legacy-mapping/LegacyMapping";
import MigrationRules from "./modules/sections/operation/migration-rules/MigrationRules";
import Enrichment from "./modules/sections/operation/enrichment/Enrichment";
import ValidationRules from "./modules/sections/operation/validation-rules/ValidationRules";
import LoadFileSetup from "./modules/sections/operation/loadfile-setup/LoadFileSetup";
import JobsOverview from "./modules/sections/batch-processing/jobs-overview/JobsOverview";
import ProcessOverview from "./modules/sections/batch-processing/process-overview/ProcessOverview";
import Account from "./modules/sections/administration/account/Account";
import UserManagement from "./modules/sections/administration/user-management/UserManagement";
import GroupManagement from "./modules/sections/administration/group-management/GroupManagement";
import DemoReport from "./modules/sections/report/demo/DemoReport";
import CustomQueries from "./modules/sections/report/custom-queries/CustomQueries";
import "./i18n";
import { useTranslation } from "react-i18next";
import history from "./modules/shared/utils/HistoryUtil";
import Notifications from "./modules/shared/Notifications";
import UsersService from "./services/admin/UsersService";
import GroupCodesService from "./services/groupcodes/GroupCodesService";
import {
  setEntityPermissions,
  setGroupCodePermissions,
  setNotifications,
  setPermissions,
  setUserHasPermissions,
  setUserPermissions,
  setDefaultPermissions,
  setAccessToken,
} from "./redux/actions";
import signalR, {
  HttpTransportType,
  HubConnectionBuilder,
} from "@microsoft/signalr";
import NotificationService from "./services/notifications/NotificationsService";
import ApplicationSettings from "./modules/sections/administration/application-settings/ApplicationSettings";
import ApplicationService from "./services/application/ApplicationService";
import UnfinishedMappings from "./modules/sections/report/unfinished-mappings/UnfinishedMappings";
import Manuals from "./modules/sections/report/manuals/Manuals";
import ReleaseNotes from "./modules/sections/report/release-notes/ReleaseNotes";
import jwtDecode from "jwt-decode";
import JwtService from "./services/jwt/JwtService";
import { PERMISSION_METHODS } from "./config/consts/permissions";
import { ROUTES } from "./config/consts/routes";

import Clustering from "./modules/sections/operation/clustering/Clustering";
import { getBaseUrl } from "./http-common";

import { AccessManagement } from "./modules/sections/excel-addin/acces-management/AccessManagement";
import { ViewManagement } from "./modules/sections/excel-addin/view-management/ViewManagement";
import QueryForm from "./modules/sections/report/custom-queries/QueryForm/QueryForm";
import Analytics from "./modules/sections/report/analytics/Analytics";
import Publish from "./modules/sections/operation/publish/Publish";

function App() {
  const [connection, setConnection] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeProcesses, setActiveProcesses] = useState([]);
  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(getBaseUrl() + "/hubs/notifications")
      .withUrl(getBaseUrl() + "/hubs/batchProcessing")
      .withAutomaticReconnect()
      .build();

    setConnection(newConnection);
  }, []);

  useEffect(() => {
    if (connection) {
      connection
        .start()
        .then((result) => {
          console.log("Connected!");
          connection.on("RefreshNotifications", (notifications) => {
            dispatch(setNotifications(notifications));
          });
          connection.on("RefreshActiveProcesses", (activeProcesses) => {
            setActiveProcesses(activeProcesses);
          });
        })
        .catch((e) => console.log("Connection failed: ", e));
    }
  }, [connection]);

  const DEFAULT_PERMISSION_METHOD: string = PERMISSION_METHODS.DELETE;
  const { t, i18n } = useTranslation("translations");

  const { groupCode, entity, profile, language, accessToken, userPermissions } =
    useSelector((state: any) => ({
      groupCode: state.groupCode,
      entity: state.entity,
      profile: state.profile,
      language: state.language,
      accessToken: state.profile?.accessToken,
      userPermissions: state.userPermissions,
    }));

  useEffect(() => {
    if (accessToken && userPermissions) {
      try {
        let groupCodeIdDecoded = jwtDecode<any>(accessToken).groupCodeId;
        let entityIdDecoded = jwtDecode<any>(accessToken).entityId;

        const groupCodeId = JSON.parse(
          groupCodeIdDecoded && groupCodeIdDecoded !== undefined
            ? groupCodeIdDecoded
            : "0"
        );
        const entityId = JSON.parse(
          entityIdDecoded && entityIdDecoded !== undefined
            ? entityIdDecoded
            : "0"
        );

        let userHasPermissions = userPermissions?.find(
          (x: any) => x.entityId === entityId && x.groupCodeId === groupCodeId
        );
        if (!userHasPermissions) {
          userHasPermissions = {
            read: true,
            write: true,
            delete: true,
          };
        }

        dispatch(setUserHasPermissions(userHasPermissions));
      } catch {
        // Temporary fallback to be backwards compatible (force refresh token)
        JwtService.refreshToken(groupCode, entity).then((response: any) => {
          dispatch(setAccessToken(response.data.token));
          dispatch(setUserPermissions(response.data.permissions));
        });
      }
    }
  }, [accessToken, userPermissions]);

  useEffect(() => {
    if (profile && language) {
      i18n.changeLanguage(language); //change the language
    }
  }, [profile, language]);

  const dispatch = useDispatch();

  return (
    <div>
      {!profile && (
        <Routes>
          <Route
            path="*"
            element={<Navigate replace to={ROUTES.general.login.route} />}
          />
          <Route path={ROUTES.general.login.route} element={<Login />} />
        </Routes>
      )}
      {profile && (
        <Layout style={{ minHeight: "100vh" }}>
          <SidebarLayout />
          <Layout
            className="main-layout site-drawer-render-in-current-wrapper"
            style={{ height: "100vh" }}
          >
            <Header className="main-layout-header">
              <Head activeProcesses={activeProcesses} />
            </Header>
            <Content className="main-layout-content">
              <Routes>
                <Route
                  path={ROUTES.general.login.route}
                  element={<Navigate replace to={ROUTES.general.main.route} />}
                />
                <Route
                  path={ROUTES.general.main.route}
                  element={<Dashboard />}
                />
                <Route
                  path={ROUTES.configuration.projectSetup.route}
                  element={<ProjectSetup />}
                />
                <Route
                  path={ROUTES.configuration.tableSetup.route}
                  element={<TableSetup />}
                />
                <Route
                  path={ROUTES.operation.import.route}
                  element={<Extraction />}
                />
                <Route
                  path={ROUTES.operation.legacyMapping.route}
                  element={<LegacyMapping />}
                />
                <Route
                  path={ROUTES.operation.migrationRules.route}
                  element={<MigrationRules />}
                />
                <Route
                  path={ROUTES.operation.validationRules.route}
                  element={<ValidationRules />}
                />
                <Route
                  path={ROUTES.operation.enrichment.route}
                  element={<Enrichment />}
                />
                <Route
                  path={ROUTES.operation.loadFileSetup.route}
                  element={<LoadFileSetup />}
                />
                <Route
                  path={ROUTES.operation.clustering.route}
                  element={<Clustering />}
                />
                <Route
                  path={ROUTES.operation.publish.route}
                  element={<Publish />}
                />
                <Route
                  path={ROUTES.batchProcessing.jobsOverview.route}
                  element={<JobsOverview />}
                />
                <Route
                  path={ROUTES.batchProcessing.processOverview.route}
                  element={
                    <ProcessOverview activeProcesses={activeProcesses} />
                  }
                />
                <Route
                  path={ROUTES.administration.account.route}
                  element={<Account />}
                />
                <Route
                  path={ROUTES.administration.userManagement.route}
                  element={<UserManagement />}
                />
                <Route
                  path={ROUTES.administration.groupManagement.route}
                  element={<GroupManagement />}
                />
                <Route
                  path={ROUTES.administration.applicationSettings.route}
                  element={<ApplicationSettings />}
                />
                <Route
                  path={ROUTES.report.customQueries.route}
                  element={<CustomQueries />}
                />
                <Route
                  path={ROUTES.report.customQuery.route}
                  element={<QueryForm />}
                />
                <Route
                  path={ROUTES.report.unfinishedMappings.route}
                  element={<UnfinishedMappings />}
                />
                <Route
                  path={ROUTES.notifications.route}
                  element={<Notifications />}
                />
                <Route
                  path={ROUTES.report.manuals.route}
                  element={<Manuals />}
                />
                <Route
                  path={ROUTES.report.releaseNotes.route}
                  element={<ReleaseNotes />}
                />
                <Route
                  path={ROUTES.excelAddin.excelSettings.route}
                  element={<AccessManagement />}
                />
                <Route
                  path={ROUTES.excelAddin.viewManagement.route}
                  element={<ViewManagement />} />
                <Route
                  path={ROUTES.report.analytics.route}
                  element={<Analytics />} />
                <Route
                  path={ROUTES.report.demo.route}
                  element={<DemoReport />}
                />
              </Routes>
            </Content>
            <Footer className="main-layout-footer">
              <Foot />
            </Footer>
          </Layout>
        </Layout>
      )}
    </div>
  );
}

// export default App;
export default () => (
  <Router history={history}>
    <App />
  </Router>
);
